/**
 * Read every sheet of an .xlsx / .xlsm / .xlsb, and every picture on it.
 *
 * Why not ExcelJS, which the export already loads: on the client's 19 MB
 * Patalganga workbook ExcelJS needs about three gigabytes of memory, which is
 * past what a browser tab is given. The import either crashed the tab or got
 * through by luck. This reads the zip directly - the sheet XML, the shared
 * strings and the drawing anchors - and holds little more than the file itself.
 *
 * What it answers, per sheet:
 *   - the cell values, 1-based, like the spreadsheet numbers them
 *   - every picture, keyed by the row it sits in, in left-to-right order
 *
 * "The row it sits in" is decided by the picture's vertical centre, using the
 * sheet's real row heights. Pictures on these sheets are dropped in by hand and
 * often start a little above the row they belong to; going by the top-left
 * corner put such a photo on the mould above.
 *
 * Image formats are decided by the bytes, not the file name. Excel stores some
 * pasted pictures as `.tmp` (they are PNGs) and keeps HD Photo / JPEG XR copies
 * as `.wdp`, which no browser can display. The first kind is read correctly;
 * the second is reported, never silently turned into a broken image.
 */
import { unzipSync } from 'fflate';
import { records, sharedStrings as xlsbSharedStrings, sheetCells, wideString } from './xlsb.js';
const EMU_PER_POINT = 12_700;
// --- image formats ---------------------------------------------------------
const FORMATS = [
    { mime: 'image/jpeg', name: 'JPEG', test: (b) => b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff },
    {
        mime: 'image/png',
        name: 'PNG',
        test: (b) => b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47,
    },
    { mime: 'image/gif', name: 'GIF', test: (b) => b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46 },
    {
        mime: 'image/webp',
        name: 'WebP',
        test: (b) => b[0] === 0x52 && b[1] === 0x49 && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50,
    },
    { mime: 'image/bmp', name: 'BMP', test: (b) => b[0] === 0x42 && b[1] === 0x4d },
    // Formats Excel can hold and a browser cannot show.
    { mime: null, name: 'JPEG XR (HD Photo)', test: (b) => b[0] === 0x49 && b[1] === 0x49 && b[2] === 0xbc },
    { mime: null, name: 'TIFF', test: (b) => (b[0] === 0x49 && b[1] === 0x49 && b[2] === 0x2a) || (b[0] === 0x4d && b[1] === 0x4d && b[2] === 0x00) },
    { mime: null, name: 'Windows metafile (EMF)', test: (b) => b[0] === 0x01 && b[1] === 0x00 && b[2] === 0x00 && b[3] === 0x00 },
    { mime: null, name: 'Windows metafile (WMF)', test: (b) => b[0] === 0xd7 && b[1] === 0xcd && b[2] === 0xc6 && b[3] === 0x9a },
];
/** What a picture really is, judged by its first bytes. */
export function sniffImage(bytes) {
    for (const f of FORMATS) {
        if (bytes.length >= 12 && f.test(bytes))
            return { mimeType: f.mime, formatName: f.name };
    }
    return { mimeType: null, formatName: 'an unrecognised format' };
}
// --- XML helpers -----------------------------------------------------------
/** XML entities, numeric references, and Excel's own `_x000D_` escapes. */
function unescapeXml(text) {
    return text
        .replace(/&(lt|gt|amp|quot|apos|#x[0-9a-f]+|#\d+);/gi, (_m, e) => {
        const k = e.toLowerCase();
        if (k === 'lt')
            return '<';
        if (k === 'gt')
            return '>';
        if (k === 'amp')
            return '&';
        if (k === 'quot')
            return '"';
        if (k === 'apos')
            return "'";
        const code = k.startsWith('#x') ? parseInt(k.slice(2), 16) : parseInt(k.slice(1), 10);
        return Number.isFinite(code) ? String.fromCodePoint(code) : '';
    })
        .replace(/_x([0-9A-Fa-f]{4})_/g, (_m, hex) => String.fromCharCode(parseInt(hex, 16)));
}
function attr(tag, name) {
    const m = new RegExp(`\\s${name}="([^"]*)"`).exec(tag);
    return m ? unescapeXml(m[1]) : undefined;
}
/** Resolve a relationship target against the part that declared it. */
function resolvePart(from, target) {
    if (target.startsWith('/'))
        return target.slice(1);
    const parts = from.split('/').slice(0, -1);
    for (const seg of target.split('/')) {
        if (seg === '..')
            parts.pop();
        else if (seg !== '.')
            parts.push(seg);
    }
    return parts.join('/');
}
function relsPathFor(part) {
    const dir = part.split('/').slice(0, -1).join('/');
    const file = part.split('/').pop();
    return `${dir}/_rels/${file}.rels`;
}
/** rId → absolute part path, from a part's .rels file. */
function readRels(files, part, decoder) {
    const out = new Map();
    const rels = files[relsPathFor(part)];
    if (!rels)
        return out;
    for (const m of decoder.decode(rels).matchAll(/<Relationship\b[^>]*>/g)) {
        const id = attr(m[0], 'Id');
        const target = attr(m[0], 'Target');
        const mode = attr(m[0], 'TargetMode');
        if (id && target && mode !== 'External')
            out.set(id, resolvePart(part, target));
    }
    return out;
}
/** "AB12" → 28 (1-based). */
function columnNumber(ref) {
    let n = 0;
    for (const ch of ref) {
        const c = ch.charCodeAt(0);
        if (c < 65 || c > 90)
            break;
        n = n * 26 + (c - 64);
    }
    return n;
}
// --- .xlsx cells -----------------------------------------------------------
function xlsxSharedStrings(xml) {
    if (!xml)
        return [];
    const out = [];
    for (const si of xml.matchAll(/<si>([\s\S]*?)<\/si>/g)) {
        // Phonetic runs (<rPh>) are a reading aid, not the text.
        const body = si[1].replace(/<rPh\b[\s\S]*?<\/rPh>/g, '');
        let text = '';
        for (const t of body.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>|<t\b[^>]*\/>/g))
            text += t[1] ?? '';
        out.push(unescapeXml(text));
    }
    return out;
}
function xlsxSheet(xml, sst) {
    const cells = new Map();
    const heights = new Map();
    let maxRow = 0;
    let maxCol = 0;
    const fmt = /<sheetFormatPr\b[^>]*>/.exec(xml);
    const defaultHeight = Number((fmt && attr(fmt[0], 'defaultRowHeight')) ?? 15) || 15;
    for (const row of xml.matchAll(/<row\b([^>]*?)(?:\/>|>([\s\S]*?)<\/row>)/g)) {
        const r = Number(attr(row[0], 'r'));
        const ht = attr(row[0], 'ht');
        if (r && ht)
            heights.set(r, Number(ht));
        const body = row[2];
        if (!body)
            continue;
        let nextCol = 1;
        for (const c of body.matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
            const ref = attr(c[0], 'r');
            const col = ref ? columnNumber(ref) : nextCol;
            nextCol = col + 1;
            const type = attr(c[0], 't');
            const inner = c[2] ?? '';
            const v = /<v>([\s\S]*?)<\/v>/.exec(inner)?.[1];
            let value = null;
            if (type === 's') {
                const i = Number(v);
                value = Number.isInteger(i) && i < sst.length ? sst[i] : null;
            }
            else if (type === 'inlineStr') {
                let text = '';
                for (const t of inner.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g))
                    text += t[1];
                value = unescapeXml(text);
            }
            else if (type === 'str') {
                value = v !== undefined ? unescapeXml(v) : null;
            }
            else if (type === 'b') {
                value = v === '1';
            }
            else if (type === 'e') {
                value = null; // #N/A and friends are not data
            }
            else if (v !== undefined) {
                const n = Number(v);
                value = Number.isFinite(n) ? n : unescapeXml(v);
            }
            if (value === null || value === '')
                continue;
            const rowNo = r || maxRow + 1;
            cells.set(`${rowNo},${col}`, value);
            if (rowNo > maxRow)
                maxRow = rowNo;
            if (col > maxCol)
                maxCol = col;
        }
    }
    const drawing = /<drawing\b[^>]*>/.exec(xml);
    return {
        cells,
        maxRow,
        maxCol,
        heights,
        defaultHeight,
        drawingRelId: drawing ? (attr(drawing[0], 'r:id') ?? null) : null,
    };
}
function anchors(xml) {
    const out = [];
    for (const m of xml.matchAll(/<xdr:(oneCellAnchor|twoCellAnchor)\b[\s\S]*?<\/xdr:\1>/g)) {
        const block = m[0];
        const from = /<xdr:from>([\s\S]*?)<\/xdr:from>/.exec(block)?.[1];
        if (!from)
            continue;
        const to = /<xdr:to>([\s\S]*?)<\/xdr:to>/.exec(block)?.[1];
        const num = (part, tag) => part ? Number(new RegExp(`<xdr:${tag}>(-?\\d+)</xdr:${tag}>`).exec(part)?.[1] ?? NaN) : NaN;
        // A picture's own blip, not one nested in a group's fill or an effect.
        const embed = /<a:blip\b[^>]*r:embed="([^"]+)"/.exec(block)?.[1];
        if (!embed)
            continue;
        const ext = /<xdr:ext\b[^>]*cy="(\d+)"/.exec(block)?.[1];
        out.push({
            fromRow: num(from, 'row'),
            fromRowOff: num(from, 'rowOff') || 0,
            toRow: to ? num(to, 'row') : null,
            toRowOff: to ? num(to, 'rowOff') || 0 : 0,
            extCy: ext ? Number(ext) : null,
            col: (num(from, 'col') || 0) + 1,
            embed,
        });
    }
    return out;
}
/**
 * Pictures by the row their centre falls in.
 *
 * `heightOf` gives a 1-based row's height in points; without it (an .xlsb,
 * whose heights we do not read) the anchor's own top row is used.
 */
function imagesForDrawing(files, drawingPath, decoder, heightOf) {
    const out = new Map();
    const part = files[drawingPath];
    if (!part)
        return out;
    const rels = readRels(files, drawingPath, decoder);
    // Cumulative row tops in EMU, built lazily as far as the pictures reach.
    const tops = [0];
    const topOf = (row0) => {
        while (tops.length <= row0) {
            const r = tops.length; // 0-based row whose top we are adding is r
            tops.push(tops[r - 1] + (heightOf ? heightOf(r) : 15) * EMU_PER_POINT);
        }
        return tops[row0];
    };
    const rowAt = (y) => {
        let r = 0;
        while (topOf(r + 1) <= y)
            r++;
        return r;
    };
    const placed = [];
    for (const a of anchors(decoder.decode(part))) {
        if (!Number.isFinite(a.fromRow))
            continue;
        const mediaPath = rels.get(a.embed);
        const bytes = mediaPath ? files[mediaPath] : undefined;
        if (!bytes)
            continue;
        let row0 = a.fromRow;
        const top = topOf(a.fromRow) + a.fromRowOff;
        if (heightOf) {
            const bottom = a.toRow !== null && Number.isFinite(a.toRow)
                ? topOf(a.toRow) + a.toRowOff
                : a.extCy !== null
                    ? top + a.extCy
                    : top;
            row0 = rowAt((top + bottom) / 2);
        }
        const { mimeType, formatName } = sniffImage(bytes);
        placed.push({ row: row0 + 1, col: a.col, top, image: { bytes, mimeType, formatName, col: a.col } });
    }
    placed.sort((x, y) => x.row - y.row || x.col - y.col || x.top - y.top);
    for (const p of placed) {
        const list = out.get(p.row) ?? [];
        // The same picture placed twice on one row is one photo.
        if (!list.some((i) => i.bytes === p.image.bytes))
            list.push(p.image);
        out.set(p.row, list);
    }
    return out;
}
// --- workbooks -------------------------------------------------------------
function readXlsx(files) {
    const decoder = new TextDecoder();
    const workbookXml = files['xl/workbook.xml'];
    if (!workbookXml)
        throw new Error('That file has no workbook in it. Was it saved by Excel?');
    const wbRels = readRels(files, 'xl/workbook.xml', decoder);
    const sstPart = Object.keys(files).find((f) => /^xl\/sharedStrings\.xml$/i.test(f));
    const sst = xlsxSharedStrings(sstPart ? decoder.decode(files[sstPart]) : undefined);
    const sheets = [];
    for (const m of decoder.decode(workbookXml).matchAll(/<sheet\b[^>]*>/g)) {
        const name = attr(m[0], 'name') ?? `Sheet ${sheets.length + 1}`;
        const relId = attr(m[0], 'r:id');
        const path = relId ? wbRels.get(relId) : undefined;
        const part = path ? files[path] : undefined;
        if (!path || !part)
            continue; // a chart sheet, or a dangling reference
        const data = xlsxSheet(decoder.decode(part), sst);
        const sheetRels = readRels(files, path, decoder);
        const drawingPath = data.drawingRelId ? sheetRels.get(data.drawingRelId) : undefined;
        const images = drawingPath
            ? imagesForDrawing(files, drawingPath, decoder, (row) => data.heights.get(row) ?? data.defaultHeight)
            : new Map();
        const rowCount = Math.max(data.maxRow, ...[...images.keys()], 0);
        sheets.push({
            name,
            rowCount,
            columnCount: data.maxCol,
            images,
            cell: (row, col) => data.cells.get(`${row},${col}`) ?? null,
        });
    }
    return { sheets };
}
const BrtBundleSh = 156;
function readXlsbWorkbook(files) {
    const decoder = new TextDecoder();
    const wbPart = files['xl/workbook.bin'];
    if (!wbPart)
        throw new Error('That .xlsb has no workbook in it. Was it saved by Excel?');
    const wbRels = readRels(files, 'xl/workbook.bin', decoder);
    const sst = xlsbSharedStrings(files['xl/sharedStrings.bin']);
    // BrtBundleSh: hsState(4) iTabID(4) strRelID(nullable wide string) strName(wide string)
    const bundles = [];
    for (const [id, payload] of records(wbPart)) {
        if (id !== BrtBundleSh || payload.length < 12)
            continue;
        const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
        const relCount = view.getUint32(8, true);
        const relId = relCount === 0xffffffff ? '' : wideString(view, payload, 8);
        const nameOffset = 8 + 4 + (relCount === 0xffffffff ? 0 : relCount * 2);
        const name = nameOffset + 4 <= payload.length ? wideString(view, payload, nameOffset) : '';
        bundles.push({ name: name || `Sheet ${bundles.length + 1}`, relId });
    }
    const sheets = [];
    for (const b of bundles) {
        const path = wbRels.get(b.relId);
        const part = path ? files[path] : undefined;
        if (!path || !part)
            continue;
        const { cells, maxRow, maxCol } = sheetCells(part, sst);
        // The drawing is linked from the sheet's rels; an .xlsb sheet carries one at most.
        const sheetRels = readRels(files, path, decoder);
        const drawingPath = [...sheetRels.values()].find((p) => /\/drawings\/[^/]+\.xml$/i.test(p));
        const images = drawingPath
            ? imagesForDrawing(files, drawingPath, decoder, null)
            : new Map();
        sheets.push({
            name: b.name,
            rowCount: Math.max(maxRow + 1, ...[...images.keys()], 0),
            columnCount: maxCol + 1,
            images,
            cell: (row, col) => cells.get(`${row - 1},${col - 1}`) ?? null,
        });
    }
    return { sheets };
}
/**
 * Read a zipped Excel workbook - .xlsx, .xlsm, .xltx or .xlsb.
 *
 * Only the parts a mould list needs are decompressed; the rest of the archive
 * (styles, themes, printer settings) is skipped unread.
 */
export function readWorkbook(data) {
    const files = unzipSync(data, {
        filter: (f) => f.name.startsWith('xl/') &&
            !/^xl\/(styles|theme|printerSettings|calcChain|externalLinks|customXml|vbaProject)/i.test(f.name),
    });
    const book = files['xl/workbook.bin'] ? readXlsbWorkbook(files) : readXlsx(files);
    if (book.sheets.length === 0)
        throw new Error('That file has no readable sheets in it.');
    return book;
}
//# sourceMappingURL=workbook.js.map